/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as SourceConstants from 'constants/SourceConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import LegacyTagsInput from 'ui-library/tags-input/LegacyTagsInput';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import pick from 'util/pick';

import FolderDAO from './FolderDAO';
import FolderSelectTree from './FolderSelectTree';

const {REGISTRY_TYPE} = SourceConstants;

interface Props {
  allFolders?: Array<any>;
  dataTestAttrs?: DataTestAttrs;
  includeHiddenProjects: boolean;
  onLoadAllFolders?: (...args: Array<any>) => any;
  onSelectFolders: (...args: Array<any>) => any;
  selectedFolderIds: Array<string>;
  showInventory: boolean;
  showNotebook: boolean;
  showRegistries: boolean;
  placeHolderText: string;
  tagsInputSize: TagsInputSize;
}

interface State {
  folders: Array<any> | null;
  open: boolean;
}

class MultipleFolderSelector extends React.Component<Props, State> {
  static defaultProps = {
    includeHiddenProjects: false,
    showInventory: false,
    showNotebook: false,
    showRegistries: true,
    placeHolderText: 'Add locations',
    tagsInputSize: TagsInputSize.LARGE,
  };

  _isMounted: any;

  state: State = {
    folders: null,
    open: false,
  };

  componentDidMount(): void {
    this._isMounted = true;
    FolderDAO.fetchAll({level: 'read'})
      .done((folders) => {
        if (this._isMounted) {
          this.setAllFolders(folders);
        }
      })
      .fail(notifications.notifyError);
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: Props): void {
    if (!_.isMatch(this.props, pick(prevProps, 'showInventory', 'showNotebook'))) {
      // TODO(T4346): The child component shouldn't be updating the parent on componentDidUpdate
      assert(this._getAllFolders() != null, 'Switched to projectsOnly before folders were loaded');
      // Switching to project-only, so filter to only projects.
      const predicate = FileBrowserUtil.propsToFolderPredicate(this.props);
      const folders = this.props.selectedFolderIds
        .map((folderId) => this._getFolder(folderId))
        .filter((folderId) => predicate(folderId));
      this.props.onSelectFolders(folders);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setAllFolders = (folders: any) => {
    let foldersToSet = folders;
    if (!this.props.showRegistries) {
      foldersToSet = folders.filter((folder: any) => folder.source.sourceType !== REGISTRY_TYPE);
    }
    if (this.props.onLoadAllFolders != null) {
      this.props.onLoadAllFolders(foldersToSet);
    }

    this.setState({folders: foldersToSet});

    // Remove any folders from the selected folders if they no longer exist.
    const folderIdLookup = _.indexBy(folders, 'api_identifier');
    const newFolders = this.props.selectedFolderIds
      // eslint-disable-next-line benchlint/no-in-operator
      .filter((id) => id in folderIdLookup)
      .map((id) => folderIdLookup[id]);

    if (newFolders.length < this.props.selectedFolderIds.length) {
      this.props.onSelectFolders(newFolders);
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _getAllFolders = () => {
    if (this.props.allFolders) {
      return this.props.allFolders;
    } else {
      return this.state.folders;
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _getFolder = (folderId: any) => {
    const folders = this._getAllFolders();
    if (folders == null) {
      return null;
    }
    return _.findWhere(folders, {api_identifier: folderId});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSelectedFolders = (newFolderIds: any) => {
    if (this._getAllFolders() == null) {
      // Don't do anything if folders haven't loaded yet
      return;
    }
    this.props.onSelectFolders(newFolderIds.map((folderId: any) => this._getFolder(folderId)));
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  toggleOpen = () => {
    this.setState({open: !this.state.open});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleBackdropMouseDown = () => {
    // Close the dropdown on outside click.
    this.setState({open: false});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _getFolderName = (folderId: any) => {
    const folder = this._getFolder(folderId);
    return folder ? folder.name : 'Loading...';
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _isWaitingForLoadingFolders = () => this._getAllFolders() == null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  renderDropdown = () => {
    if (this._isWaitingForLoadingFolders()) {
      return <div className="faded u-tiny-padding">Loading...</div>;
    }
    const folders = this._getAllFolders();
    const predicate = FileBrowserUtil.propsToFolderPredicate(this.props);
    return (
      <FolderSelectTree
        folderIds={this.props.selectedFolderIds}
        onChangeSelectedFolders={this.updateSelectedFolders}
        folders={folders!.filter((folder) => predicate(folder))}
        multiple={true}
        submitHandler={noop}
        includeHiddenProjects={this.props.includeHiddenProjects}
      />
    );
  };

  render(): JSX.Element {
    return (
      <div className="multipleFolderSelector" {...getTestProps(this.props.dataTestAttrs)}>
        {this.state.open && <div className="backdrop" onMouseDown={this.handleBackdropMouseDown} />}
        {this.state.open && <div className="multipleFolderSelector-dropdown">{this.renderDropdown()}</div>}
        <div className="u-pointer" onClick={this.toggleOpen}>
          <LegacyTagsInput
            allowAdding={false}
            allowSearching={false}
            inputClass="u-pointer"
            value={this.props.selectedFolderIds.map((id) => ({text: this._getFolderName(id), id}))}
            onChange={(options: any) => {
              // eslint-disable-next-line no-restricted-syntax
              this.updateSelectedFolders(_.pluck(options, 'id'));
            }}
            placeholder={this._isWaitingForLoadingFolders() ? 'Loading...' : this.props.placeHolderText}
            size={this.props.tagsInputSize}
            dataTestAttrs={{component: 'MultipleFolderSelector', element: 'root'}}
          />
        </div>
      </div>
    );
  }
}

export default MultipleFolderSelector;
